import { Component, OnInit, NgZone, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Job } from '../../../domain/models/job';
import { RouteImport } from '../../../domain/models/route-import';
import { RouteImportType } from '../../../domain/enums/routeImportType';
import * as _ from 'lodash';

@Component({
  selector: 'app-route-import',
  templateUrl: './route-import.component.html',
  styleUrls: ['./route-import.component.scss']
})
export class RouteImportComponent implements OnInit {
  @Input() public name: string;
  @Input() public routeImportType: RouteImportType;
  @Input() public addedRoutes: RouteImport[];
  @Input() public availableRoutes: RouteImport[];
  @Output() public onSaveRoutes: EventEmitter<RouteImport[]>;
  @Output() public onCancel: EventEmitter<void>;

  private existingRoutes: RouteImport[];
  private formLocked: boolean;

  private editingRoute: RouteImport;

  constructor(private zone: NgZone) {
    this.onSaveRoutes = new EventEmitter<RouteImport[]>();
    this.onCancel = new EventEmitter<void>();
  }

  ngOnInit() {
    this.formLocked = false;
    this.existingRoutes = _.cloneDeep(this.addedRoutes);
  }

  addRoutes(): void {
    this.stopEditing(this.editingRoute);

    let routesToRemove: RouteImport[] = [];
    _.each(this.availableRoutes, (item: RouteImport) => {
      if (item.selected && this.addedRoutes.indexOf(item) === -1) {
        item.minimumDelay = 1;
        this.addedRoutes.push(item);
        routesToRemove.push(item);
      }
    });
    this.removeAvailableRoutes(routesToRemove);
    this.addedRoutes = this.orderAscending(this.addedRoutes);
  }

  removeAddedRoute(item: RouteImport) {
    let index = this.addedRoutes.indexOf(item);
    if (index !== -1) {
      this.addedRoutes.splice(index, 1);
      this.addAvailableRoute(item);
    }
  }

  itemsSelected(): boolean {
    return _.some(this.availableRoutes, (item: RouteImport) => {
      return item.selected;
    });
  }

  formIsValid(): boolean {
    return this.hasRoutes();
  }

  private hasRoutes(): boolean {
    return (this.availableRoutes && this.availableRoutes.length > 0) || (this.addedRoutes && this.addedRoutes.length > 0);
  }

  saveRoutes(): void {
    if (this.formLocked) { return; }

    this.stopEditing(this.editingRoute);

    this.formLocked = true;
    let result: RouteImport[] = [];

    _.each(this.addedRoutes, (item: RouteImport) => {
      const routeExists: boolean = _.some(this.existingRoutes, (route: RouteImport) => {
        return item.id === route.id;
      });

      item.added = !routeExists;
      result.push(item);
    });

    _.each(this.availableRoutes, (item: RouteImport) => {
      const routeExists: boolean = _.some(this.existingRoutes, (route: RouteImport) => {
        return route.id === item.id;
      });

      if (routeExists) {
        item.toRemove = true;
        result.push(item);
      }
    });

    this.onSaveRoutes.emit(result);
  }

  cancel(): void {
    this.stopEditing(this.editingRoute);
    this.onCancel.emit();
  }

  private addAvailableRoute(item: RouteImport) {
    item.selected = false;
    if (this.availableRoutes.indexOf(item) === -1) {
      this.availableRoutes.push(item);
      this.availableRoutes = this.orderAscending(this.availableRoutes);
    }
  }

  private removeAvailableRoutes(items: RouteImport[]) {
    _.each(items, (item: RouteImport) => {
      const index = this.availableRoutes.indexOf(item);
      if (index !== -1) {
        this.availableRoutes.splice(index, 1);
      }
    });
  }

  private orderAscending(list: RouteImport[]): RouteImport[] {
    return _.orderBy(list, ['name'], ['asc']);
  }

  isProject(): boolean {
    return this.routeImportType === RouteImportType.Project;
  }

  isDashboard(): boolean {
    return this.routeImportType === RouteImportType.Dashboard;
  }

  editRoute(route: RouteImport): void {
    route.displayName = route.name;
    route.editedMinimumDelay = route.minimumDelay;
    this.editingRoute = route;
    let input = <HTMLInputElement>document.getElementById(route.id);
    if (input) {
      setTimeout(() => {
        input.select();
      }, 0);
    }
  }

  updateRoute(route: RouteImport): void {
    if (route.editedMinimumDelay !== null && route.editedMinimumDelay !== undefined) {
      route.minimumDelay = route.editedMinimumDelay > 0 ? route.editedMinimumDelay : 1;
    }

    if (route.displayName && route.displayName.length > 0) {
      route.name = route.displayName;
      this.stopEditing(route);
    }
  }

  stopEditing(route: RouteImport): void {
    if (route) {
      route.displayName = null;
    }
    this.editingRoute = null;
  }

  isEditing(routeId: string): boolean {
    return this.editingRoute && routeId === this.editingRoute.id;
  }

  nameIsInvalid(displayName: string): boolean {
    return displayName.length === 0;
  }

  onKeydown(route: RouteImport, event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
        this.updateRoute(route);
        break;
      case 'Escape':
        this.stopEditing(route);
        break;
    }
    event.stopPropagation();
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/shared/components/route-import/route-import.component.ts